// category:
//   "starter"      — always spawn at extraction + material_site nodes; available from game start
//   "discoverable" — spawn ONLY at material_site nodes; locked until player visits that node
//   "drop_only"    — drop from extraction nodes / enemies; never used as smelt recipe inputs

export const DEBRIS_ITEMS = [
  // ── Starter base mats (T1) ────────────────────────────────────────────────
  { id: "copper",           name: "Copper",            icon: "⚡",  color: "#e8a44a", rarity: 1,    category: "starter" },
  { id: "hull_debris",      name: "Hull Debris",       icon: "🛡️", color: "#8899aa", rarity: 1,    category: "starter" },
  { id: "glass_shard",      name: "Glass Shard",       icon: "💎",  color: "#88ccee", rarity: 1,    category: "starter" },

  // ── Discoverable base mats (T2) ───────────────────────────────────────────
  // These appear ONLY at material_site nodes; each one unlocks new smelt recipes.
  { id: "silica_dust",      name: "Silica Dust",       icon: "🌫️",  color: "#ccddee", rarity: 0.9,  category: "discoverable" },
  { id: "titanium_ore",     name: "Titanium Ore",      icon: "🪨",  color: "#aabbcc", rarity: 0.9,  category: "discoverable" },
  { id: "isotope8",         name: "Isotope 8",         icon: "☢️",  color: "#88ff44", rarity: 0.9,  category: "discoverable" },

  // ── Discoverable base mats (T3) ───────────────────────────────────────────
  { id: "dark_matter",      name: "Dark Matter",       icon: "🌑",  color: "#445566", rarity: 0.75, category: "discoverable" },
  { id: "living_metal",     name: "Living Metal",      icon: "🌿",  color: "#44ff88", rarity: 0.75, category: "discoverable" },
  { id: "plasma_condensate",name: "Plasma Condensate", icon: "🔥",  color: "#ff7722", rarity: 0.75, category: "discoverable" },

  // ── Drop-only rares ───────────────────────────────────────────────────────
  { id: "panel",  name: "Solar Panel", icon: "☀️",  color: "#ffcc33", rarity: 0.35, category: "drop_only" },
  { id: "cable",  name: "Data Cable",  icon: "🔌",  color: "#77bb55", rarity: 0.4,  category: "drop_only" },
  { id: "shard",  name: "Void Shard",  icon: "🌀",  color: "#aa55ff", rarity: 0.15, category: "drop_only" },
];

// ── Tier 1 Refined ────────────────────────────────────────────────────────────
// Includes both T1 refined alloys and T1 components (Forge Level 5)
export const TIER1_MATERIALS = [
  { id: "fuel",              name: "Fuel",               icon: "⛽",  color: "#ffaa00", tier: 1 },
  { id: "metal_alloy",       name: "Metal Alloy",        icon: "⬛",  color: "#99aabb", tier: 1 },
  { id: "synth_alloy",       name: "Synthesized Alloy",  icon: "🟫",  color: "#bb8855", tier: 1 },
  { id: "thermal_core",      name: "Thermal Core",       icon: "🔥",  color: "#ff5500", tier: 1 },
  { id: "ionized_lens",      name: "Ionized Lens",       icon: "🔷",  color: "#44aaff", tier: 1 },
  { id: "reinforced_frame",  name: "Reinforced Frame",   icon: "🔩",  color: "#aabbcc", tier: 1 },
  { id: "plated_wiring",     name: "Plated Wiring",      icon: "🔌",  color: "#ddaa44", tier: 1 },
  { id: "optical_processor", name: "Optical Processor",  icon: "🔹",  color: "#88eeff", tier: 1 },
  { id: "pressure_valve",    name: "Pressure Valve",     icon: "⚙️",  color: "#ccbbaa", tier: 1 },
];

// ── Tier 2 Refined ────────────────────────────────────────────────────────────
export const TIER2_MATERIALS = [
  { id: "nano_glass",        name: "Nano-Glass",         icon: "🕸️",  color: "#55eebb", tier: 2 },
  { id: "titanium_plates",   name: "Titanium Plates",    icon: "🛡️",  color: "#778899", tier: 2 },
  { id: "enriched_cells",    name: "Enriched Cells",     icon: "🔴",  color: "#ff6644", tier: 2 },
  { id: "warp_coolant",      name: "Warp Coolant",       icon: "💠",  color: "#44ccff", tier: 2 },
  { id: "kinetic_shielding", name: "Kinetic Shielding",  icon: "🔰",  color: "#88aaff", tier: 2 },
  { id: "neural_link",       name: "Neural Link",        icon: "🧠",  color: "#cc88ff", tier: 2 },
];

// ── Tier 3 Refined ────────────────────────────────────────────────────────────
export const TIER3_MATERIALS = [
  { id: "void_matter",       name: "Void Matter",        icon: "🌀",  color: "#6633aa", tier: 3 },
  { id: "bio_steel",         name: "Bio-Steel",          icon: "🌿",  color: "#33bb66", tier: 3 },
  { id: "star_fire",         name: "Star-Fire",          icon: "⭐",  color: "#ff8800", tier: 3 },
  { id: "dimensional_tank",  name: "Dimensional Tank",   icon: "🌑",  color: "#445566", tier: 3 },
  { id: "self_healing_hull", name: "Self-Healing Hull",  icon: "💚",  color: "#44ff88", tier: 3 },
  { id: "quantum_core",      name: "Quantum Core",       icon: "✨",  color: "#ffddff", tier: 3 },
];

// Flat list used in UI panels that need all smelted materials
export const BASE_MATERIALS = [...TIER1_MATERIALS, ...TIER2_MATERIALS, ...TIER3_MATERIALS];

// Ordered list of discoverable mats — the index is used in sector.js to assign
// a mat to each material_site node deterministically (round-robin by site count).
export const DISCOVERABLE_MATS = DEBRIS_ITEMS.filter(d => d.category === "discoverable");
